import type { MatchProposal } from '@/types/tournament-round.model';

interface TimedProposal {
  proposal: MatchProposal;
  startMs: number | null;
  endMs: number | null;
}

/**
 * Detects schedule overlaps between matches as a complementary warning.
 * Two matches of the same team at overlapping times -> overlapWarning = true.
 * Does not touch `conflict`: that flag is the real forced state reported by the backend.
 */
export function markProposalConflicts(
  proposals: MatchProposal[],
  matchDurationMinutes: number,
): MatchProposal[] {
  // Convert the proposal into objects with timestamps to compare overlaps
  const withTimes: TimedProposal[] = proposals.map((proposal) => {
    if (!proposal.date || !proposal.startTime) {
      return { proposal, startMs: null, endMs: null };
    }
    const startMs = new Date(`${proposal.date}T${proposal.startTime}:00`).getTime();
    const endMs = startMs + matchDurationMinutes * 60_000;
    return { proposal, startMs, endMs };
  });

  // Mark overlaps: if two matches share a team and overlap in time
  return withTimes.map((a, i) => {
    if (a.startMs === null) return a.proposal;

    const hasOverlap = withTimes.some((b, j) => {
      if (i === j || b.startMs === null) return false;
      const sameTeam =
        a.proposal.team1Name === b.proposal.team1Name ||
        a.proposal.team1Name === b.proposal.team2Name ||
        a.proposal.team2Name === b.proposal.team1Name ||
        a.proposal.team2Name === b.proposal.team2Name;
      if (!sameTeam) return false;
      // Overlap: a.start < b.end && b.start < a.end
      return a.startMs! < b.endMs! && b.startMs! < a.endMs!;
    });

    return { ...a.proposal, overlapWarning: hasOverlap };
  });
}
